import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Plus, Trash2, ChevronDown, ChevronRight,
  Wallet, DollarSign, TrendingDown, ArrowUpCircle,
  Printer, Calendar, Building2, Pencil, Check, X, Database,
  Upload, Loader, Camera, FileCheck, FileX, Paperclip,
} from 'lucide-react';
import { currencySymbol, fmtNum } from '../utils/finance';
import { extractReceipt } from '../utils/extractReceipt';
import { fmtMonth } from '../utils/dateFormat';
import ClaimStatement from '../components/ClaimStatement';

const CATEGORIES = [
  'Transport', 'F&B', 'Supplies', 'Equipment', 'Marketing',
  'Maintenance', 'Utilities', 'Professional Services', 'Travel', 'Other',
];

export default function PettyCashPage({
  user,
  pettyCash,
  balance,
  totalTopUps,
  totalExpenses,
  businesses,
  addExpense,
  updateExpense,
  deleteExpense,
  addTopUp,
  updateSettings,
  savePettyCash,
}) {
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [showTopUpForm, setShowTopUpForm] = useState(false);
  const [showStatement, setShowStatement] = useState(false);
  const [expandedExpense, setExpandedExpense] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [extracting, setExtracting] = useState(false);

  // Default statement month to latest month with data
  const allDataMonths = pettyCash ? [...new Set([
    ...pettyCash.expenses.map((e) => e.date.slice(0, 7)),
    ...pettyCash.topUps.map((t) => t.date.slice(0, 7)),
  ])].sort().reverse() : [];
  const [statementMonth, setStatementMonth] = useState(
    allDataMonths[0] || new Date().toISOString().slice(0, 7)
  );

  if (!pettyCash) return null;

  const sym = currencySymbol(pettyCash.currency);

  const [expForm, setExpForm] = useState({
    description: '',
    amount: '',
    category: 'Other',
    businessId: '',
    date: new Date().toISOString().slice(0, 10),
  });

  const [topUpForm, setTopUpForm] = useState({
    amount: '',
    date: new Date().toISOString().slice(0, 10),
    note: '',
  });

  const handleAddExpense = (e) => {
    e.preventDefault();
    if (!expForm.description || !expForm.amount) return;
    addExpense({
      ...expForm,
      amount: parseFloat(expForm.amount),
      currency: pettyCash.currency,
      businessId: expForm.businessId || null,
      receiptUrl: null,
    });
    setExpForm({ description: '', amount: '', category: 'Other', businessId: '', date: new Date().toISOString().slice(0, 10) });
    setShowExpenseForm(false);
  };

  const handleAddTopUp = (e) => {
    e.preventDefault();
    if (!topUpForm.amount) return;
    addTopUp({
      amount: parseFloat(topUpForm.amount),
      date: topUpForm.date,
      note: topUpForm.note,
    });
    setTopUpForm({ amount: '', date: new Date().toISOString().slice(0, 10), note: '' });
    setShowTopUpForm(false);
  };

  const getBusinessName = (id) => {
    if (!id) return 'General / NK';
    const biz = businesses.find((b) => b.id === id);
    return biz ? biz.name : id;
  };

  const handleReceiptUpload = async (file) => {
    if (!file) return;
    setExtracting(true);
    setShowExpenseForm(true);
    setShowTopUpForm(false);
    try {
      const result = await extractReceipt(file);
      if (result) {
        // Try to match vendor/description to a business
        let matchedBizId = '';
        if (result.vendor) {
          const vendorLower = result.vendor.toLowerCase();
          const match = businesses.find((b) =>
            vendorLower.includes(b.name.toLowerCase()) ||
            b.name.toLowerCase().includes(vendorLower)
          );
          if (match) matchedBizId = match.id;
        }

        setExpForm({
          description: result.vendor ? `${result.vendor}${result.description ? ' - ' + result.description : ''}` : (result.description || ''),
          amount: result.amount || '',
          category: result.category || 'Other',
          businessId: matchedBizId,
          date: result.date || new Date().toISOString().slice(0, 10),
        });
      }
    } catch (err) {
      console.error('Receipt extraction failed:', err);
    }
    setExtracting(false);
  };

  // Normalize legacy single-receipt shape into multi-receipt array
  const getReceipts = (exp) => {
    if (Array.isArray(exp.receipts) && exp.receipts.length) return exp.receipts;
    if (exp.receiptUrl) return [{ url: exp.receiptUrl, name: exp.receiptName || 'Attached' }];
    return [];
  };

  // Attach one or many files at once. Existing receipts are preserved + new ones appended.
  const handleAttachReceipt = async (expenseId, files) => {
    const list = files instanceof FileList ? Array.from(files) : (Array.isArray(files) ? files : files ? [files] : []);
    if (list.length === 0) return;
    const newReceipts = await Promise.all(
      list.map((file) => new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve({ url: reader.result, name: file.name });
        reader.readAsDataURL(file);
      }))
    );
    const exp = pettyCash.expenses.find((e) => e.id === expenseId);
    const existing = exp ? getReceipts(exp) : [];
    updateExpense(expenseId, {
      receipts: [...existing, ...newReceipts],
      // Clear legacy fields so the new array is the single source of truth
      receiptUrl: null,
      receiptName: null,
    });
  };

  const removeReceipt = (expenseId, index = null) => {
    const exp = pettyCash.expenses.find((e) => e.id === expenseId);
    if (!exp) return;
    if (index === null) {
      // Remove all
      updateExpense(expenseId, { receipts: [], receiptUrl: null, receiptName: null });
      return;
    }
    const next = getReceipts(exp).filter((_, i) => i !== index);
    updateExpense(expenseId, { receipts: next, receiptUrl: null, receiptName: null });
  };

  // Receipt stats
  const expenseCount = pettyCash.expenses.length;
  const withReceipt = pettyCash.expenses.filter((e) => getReceipts(e).length > 0).length;
  const missingReceipt = expenseCount - withReceipt;

  // Group expenses by month
  const expensesByMonth = {};
  for (const exp of pettyCash.expenses) {
    const month = exp.date.slice(0, 7);
    if (!expensesByMonth[month]) expensesByMonth[month] = [];
    expensesByMonth[month].push(exp);
  }
  const sortedMonths = Object.keys(expensesByMonth).sort().reverse();

  // Available months for statement
  const availableMonths = [...new Set([
    ...pettyCash.expenses.map((e) => e.date.slice(0, 7)),
    ...pettyCash.topUps.map((t) => t.date.slice(0, 7)),
  ])].sort().reverse();

  return (
    <div className="page-container">
      <header className="dashboard-header">
        <div className="header-left">
          <Link to="/" className="btn btn-ghost back-btn">
            <ArrowLeft size={18} />
          </Link>
          <div>
            <h1 className="business-title">Petty Cash</h1>
            <span className="business-type-badge">Cross-business fund</span>
          </div>
        </div>
        <div className="header-right">
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setShowStatement(!showStatement)}
            style={{ color: 'rgba(255,255,255,0.8)' }}
          >
            <Printer size={16} /> Statement
          </button>
        </div>
      </header>

      <main className="dashboard-main">
        {/* Summary cards */}
        <div className="pc-summary-grid">
          <div className="pc-summary-card">
            <ArrowUpCircle size={20} className="pc-card-icon" />
            <div>
              <span className="pc-card-label">Total Funded</span>
              <span className="pc-card-value">{sym}{fmtNum(totalTopUps)}</span>
            </div>
          </div>
          <div className="pc-summary-card">
            <TrendingDown size={20} className="pc-card-icon" />
            <div>
              <span className="pc-card-label">Total Spent</span>
              <span className="pc-card-value spent">{sym}{fmtNum(totalExpenses)}</span>
            </div>
          </div>
          <div className="pc-summary-card highlight">
            <Wallet size={20} className="pc-card-icon" />
            <div>
              <span className="pc-card-label">Balance</span>
              <span className={`pc-card-value ${balance >= 0 ? 'positive' : 'negative'}`}>
                {sym}{fmtNum(balance)}
              </span>
            </div>
          </div>
          <div className={`pc-summary-card ${missingReceipt > 0 ? '' : 'highlight'}`}>
            {missingReceipt > 0 ? <FileX size={20} className="pc-card-icon" style={{ color: 'var(--warm-gold)' }} /> : <FileCheck size={20} className="pc-card-icon" />}
            <div>
              <span className="pc-card-label">Receipts</span>
              <span className={`pc-card-value ${missingReceipt > 0 ? 'warning' : 'positive'}`}>
                {withReceipt}/{expenseCount}
              </span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                {missingReceipt > 0 ? `${missingReceipt} missing` : 'All attached'}
              </span>
            </div>
          </div>
        </div>

        {/* Statement panel */}
        {showStatement && (
          <div className="statement-panel">
            <div className="statement-controls">
              <div className="form-field">
                <label>Statement Month</label>
                <select
                  value={statementMonth}
                  onChange={(e) => setStatementMonth(e.target.value)}
                >
                  {availableMonths.map((m) => (
                    <option key={m} value={m}>
                      {fmtMonth(m)}
                    </option>
                  ))}
                </select>
              </div>
              <ClaimStatement
                pettyCash={pettyCash}
                month={statementMonth}
                businesses={businesses}
              />
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="pc-actions">
          <button className="btn btn-primary" onClick={() => { setShowExpenseForm(true); setShowTopUpForm(false); }}>
            <Plus size={18} /> New Expense
          </button>
          <label className="btn btn-primary receipt-upload-btn">
            {extracting ? <Loader size={18} className="spin" /> : <Camera size={18} />}
            {extracting ? 'Analyzing...' : 'Upload Receipt'}
            <input
              type="file"
              accept="image/*,.pdf"
              hidden
              onChange={(e) => e.target.files[0] && handleReceiptUpload(e.target.files[0])}
              disabled={extracting}
            />
          </label>
          <button className="btn btn-secondary" onClick={() => { setShowTopUpForm(true); setShowExpenseForm(false); }}>
            <ArrowUpCircle size={18} /> Top-up
          </button>
        </div>

        {/* Expense form */}
        {showExpenseForm && (
          <form className="claim-form" onSubmit={handleAddExpense}>
            <div className="claim-form-grid">
              <div className="form-field">
                <label>Description</label>
                <input
                  type="text"
                  value={expForm.description}
                  onChange={(e) => setExpForm({ ...expForm, description: e.target.value })}
                  placeholder="What was this expense for?"
                  required
                  autoFocus
                />
              </div>
              <div className="form-field">
                <label>Amount ({pettyCash.currency})</label>
                <input
                  type="number"
                  step="0.01"
                  value={expForm.amount}
                  onChange={(e) => setExpForm({ ...expForm, amount: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </div>
              <div className="form-field">
                <label>Category</label>
                <select
                  value={expForm.category}
                  onChange={(e) => setExpForm({ ...expForm, category: e.target.value })}
                >
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-field">
                <label>Business / Project</label>
                <select
                  value={expForm.businessId}
                  onChange={(e) => setExpForm({ ...expForm, businessId: e.target.value })}
                >
                  <option value="">General / NK</option>
                  {businesses.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label>Date</label>
                <input
                  type="date"
                  value={expForm.date}
                  onChange={(e) => setExpForm({ ...expForm, date: e.target.value })}
                />
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">Add Expense</button>
              <button type="button" className="btn btn-ghost" onClick={() => setShowExpenseForm(false)}>Cancel</button>
            </div>
          </form>
        )}

        {/* Top-up form */}
        {showTopUpForm && (
          <form className="claim-form" onSubmit={handleAddTopUp}>
            <div className="claim-form-grid">
              <div className="form-field">
                <label>Amount ({pettyCash.currency})</label>
                <input
                  type="number"
                  step="0.01"
                  value={topUpForm.amount}
                  onChange={(e) => setTopUpForm({ ...topUpForm, amount: e.target.value })}
                  placeholder="0.00"
                  required
                  autoFocus
                />
              </div>
              <div className="form-field">
                <label>Date</label>
                <input
                  type="date"
                  value={topUpForm.date}
                  onChange={(e) => setTopUpForm({ ...topUpForm, date: e.target.value })}
                />
              </div>
              <div className="form-field">
                <label>Note</label>
                <input
                  type="text"
                  value={topUpForm.note}
                  onChange={(e) => setTopUpForm({ ...topUpForm, note: e.target.value })}
                  placeholder="e.g. Q2 top-up, reimbursement"
                />
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">Add Top-up</button>
              <button type="button" className="btn btn-ghost" onClick={() => setShowTopUpForm(false)}>Cancel</button>
            </div>
          </form>
        )}

        {/* Expenses by month */}
        {sortedMonths.map((month) => {
          const monthExps = expensesByMonth[month];
          const monthTotal = monthExps.reduce((s, e) => s + e.amount, 0);
          const monthLabel = fmtMonth(month);
          const monthMissing = monthExps.filter((e) => !e.receiptUrl).length;

          return (
            <div key={month} className="pc-month-group">
              <div className="pc-month-header">
                <Calendar size={16} />
                <span className="pc-month-label">{monthLabel}</span>
                {monthMissing > 0 && (
                  <span className="receipt-month-missing" title={`${monthMissing} receipts missing`}>
                    <FileX size={12} /> {monthMissing}
                  </span>
                )}
                <span className="pc-month-total">{sym}{fmtNum(monthTotal)}</span>
              </div>

              <div className="claims-list">
                {monthExps.sort((a, b) => b.date.localeCompare(a.date)).map((exp) => (
                  <div key={exp.id} className="claim-row">
                    <button
                      className="claim-row-header"
                      onClick={() => setExpandedExpense(expandedExpense === exp.id ? null : exp.id)}
                    >
                      <div className="claim-row-left">
                        {expandedExpense === exp.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        <span className="claim-date mono">{exp.date}</span>
                        <span className="claim-desc">{exp.description}</span>
                      </div>
                      <div className="claim-row-right">
                        {(() => {
                          const rs = getReceipts(exp);
                          if (rs.length === 0) return <span className="receipt-badge missing" title="No receipt"><FileX size={12} /></span>;
                          return <span className="receipt-badge attached" title={`${rs.length} receipt${rs.length === 1 ? '' : 's'} attached`}><Paperclip size={12} />{rs.length > 1 ? <span style={{ marginLeft: 3, fontSize: 11, fontWeight: 700 }}>{rs.length}</span> : null}</span>;
                        })()}
                        <span className="pc-biz-badge">
                          <Building2 size={12} /> {getBusinessName(exp.businessId)}
                        </span>
                        <span className="claim-category-badge">{exp.category}</span>
                        <span className="claim-amount mono">{sym}{fmtNum(exp.amount)}</span>
                      </div>
                    </button>

                    {expandedExpense === exp.id && editingExpense !== exp.id && (
                      <div className="claim-details">
                        <div className="claim-detail-row">
                          <span className="detail-label">Business</span>
                          <span>{getBusinessName(exp.businessId)}</span>
                        </div>
                        <div className="claim-detail-row">
                          <span className="detail-label">Category</span>
                          <span>{exp.category}</span>
                        </div>
                        <div className="claim-detail-row" style={{ alignItems: 'flex-start' }}>
                          <span className="detail-label">Receipts</span>
                          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {(() => {
                              const rs = getReceipts(exp);
                              return (
                                <>
                                  {rs.map((r, i) => (
                                    <div key={i} className="receipt-attached-info" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                      <Paperclip size={13} />
                                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name || `Receipt ${i + 1}`}</span>
                                      <button className="btn btn-ghost btn-sm" onClick={() => {
                                        const win = window.open();
                                        const isPdf = (r.url || '').startsWith('data:application/pdf') || /\.pdf$/i.test(r.name || '');
                                        if (isPdf) {
                                          win.document.write(`<embed src="${r.url}" type="application/pdf" width="100%" height="100%"/>`);
                                        } else {
                                          win.document.write(`<img src="${r.url}" style="max-width:100%;"/>`);
                                        }
                                      }} style={{ fontSize: 11 }}>View</button>
                                      <button className="btn btn-ghost btn-sm" onClick={() => removeReceipt(exp.id, i)} style={{ color: 'var(--soft-red)', fontSize: 11 }}>Remove</button>
                                    </div>
                                  ))}
                                  <label className="btn btn-ghost btn-sm receipt-attach-btn" style={{ alignSelf: 'flex-start' }}>
                                    <Upload size={13} /> {rs.length === 0 ? 'Attach receipt' : 'Add another receipt'}
                                    <input type="file" accept="image/*,.pdf" multiple hidden onChange={(e) => e.target.files.length && handleAttachReceipt(exp.id, e.target.files)} />
                                  </label>
                                </>
                              );
                            })()}
                          </div>
                        </div>
                        <div className="claim-detail-actions">
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => {
                              setEditingExpense(exp.id);
                              setEditForm({
                                description: exp.description,
                                amount: exp.amount,
                                category: exp.category,
                                businessId: exp.businessId || '',
                                date: exp.date,
                              });
                            }}
                          >
                            <Pencil size={14} /> Edit
                          </button>
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => deleteExpense(exp.id)}
                            style={{ color: '#9B6B5E' }}
                          >
                            <Trash2 size={14} /> Delete
                          </button>
                        </div>
                      </div>
                    )}

                    {editingExpense === exp.id && (
                      <div className="claim-details edit-mode">
                        <div className="claim-form-grid">
                          <div className="form-field">
                            <label>Description</label>
                            <input
                              type="text"
                              value={editForm.description}
                              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                            />
                          </div>
                          <div className="form-field">
                            <label>Amount</label>
                            <input
                              type="number"
                              step="0.01"
                              value={editForm.amount}
                              onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                            />
                          </div>
                          <div className="form-field">
                            <label>Category</label>
                            <select
                              value={editForm.category}
                              onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                            >
                              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </div>
                          <div className="form-field">
                            <label>Business</label>
                            <select
                              value={editForm.businessId}
                              onChange={(e) => setEditForm({ ...editForm, businessId: e.target.value })}
                            >
                              <option value="">General / NK</option>
                              {businesses.map((b) => (
                                <option key={b.id} value={b.id}>{b.name}</option>
                              ))}
                            </select>
                          </div>
                          <div className="form-field">
                            <label>Date</label>
                            <input
                              type="date"
                              value={editForm.date}
                              onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                            />
                          </div>
                        </div>
                        <div className="form-actions">
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => {
                              updateExpense(exp.id, {
                                ...editForm,
                                amount: parseFloat(editForm.amount),
                                businessId: editForm.businessId || null,
                              });
                              setEditingExpense(null);
                            }}
                          >
                            <Check size={14} /> Save
                          </button>
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => setEditingExpense(null)}
                          >
                            <X size={14} /> Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {pettyCash.expenses.length === 0 && (
          <div className="empty-state">
            <DollarSign size={36} />
            <p>No expenses recorded yet.</p>
            {savePettyCash && (
              <button
                className="btn btn-secondary"
                style={{ marginTop: 16 }}
                onClick={() => {
                  const findBiz = (keyword) => {
                    const b = businesses.find((bz) => bz.name?.toLowerCase().includes(keyword));
                    return b ? b.id : null;
                  };
                  const kngId = findBiz('khao') || findBiz('kng') || findBiz('gym');
                  const rrId = findBiz('reef') || findBiz('river') || findBiz('fishing');

                  savePettyCash({
                    initialFloat: 11500,
                    currency: 'SGD',
                    topUps: [
                      { id: 'topup-1', amount: 11500, date: '2026-01-01', note: 'Initial petty cash float' },
                    ],
                    expenses: [
                      { id: 'exp-1', description: 'Muay Thai', amount: 1500.00, currency: 'SGD', category: 'Other', businessId: kngId, date: '2026-01-30', receiptUrl: null },
                      { id: 'exp-2', description: 'Phone Bill', amount: 86.06, currency: 'SGD', category: 'Utilities', businessId: null, date: '2026-01-31', receiptUrl: null },
                      { id: 'exp-3', description: 'Lure Haven - 17x line cleaning with spool + 12x without spool', amount: 460.00, currency: 'SGD', category: 'Equipment', businessId: rrId, date: '2026-02-02', receiptUrl: null },
                      { id: 'exp-4', description: 'Laptop Lenovo X1 Carbon', amount: 2899.40, currency: 'SGD', category: 'Equipment', businessId: null, date: '2026-02-11', receiptUrl: null },
                      { id: 'exp-5', description: 'Lure Haven - 2x Varivas P.E Line Avani Jigging Max Power 10x10 X9 300m', amount: 128.25, currency: 'SGD', category: 'Equipment', businessId: rrId, date: '2026-02-12', receiptUrl: null },
                      { id: 'exp-6', description: 'Snow Monsters - Snowboarding Lesson (Nigel)', amount: 88.00, currency: 'SGD', category: 'Other', businessId: null, date: '2026-02-24', receiptUrl: null },
                      { id: 'exp-7', description: 'Snow Monsters - Snowboarding Lesson (Jolene)', amount: 88.00, currency: 'SGD', category: 'Other', businessId: null, date: '2026-02-24', receiptUrl: null },
                      { id: 'exp-8', description: 'Decathlon - 4x Snowboarding Pads + Butt Pad', amount: 146.40, currency: 'SGD', category: 'Equipment', businessId: null, date: '2026-02-25', receiptUrl: null },
                      { id: 'exp-9', description: 'Snow Monsters - 30 sessions', amount: 1800.00, currency: 'SGD', category: 'Other', businessId: null, date: '2026-02-26', receiptUrl: null },
                      { id: 'exp-10', description: 'Snow Monsters - Snowboarding Lesson (Nigel)', amount: 88.00, currency: 'SGD', category: 'Other', businessId: null, date: '2026-02-26', receiptUrl: null },
                      { id: 'exp-11', description: 'Snow Monsters - Snowboarding Lesson (Jolene)', amount: 88.00, currency: 'SGD', category: 'Other', businessId: null, date: '2026-02-26', receiptUrl: null },
                      { id: 'exp-12', description: 'Phone Bill', amount: 36.75, currency: 'SGD', category: 'Utilities', businessId: null, date: '2026-02-27', receiptUrl: null },
                      { id: 'exp-13', description: 'Anthropic AI - Subscription', amount: 7.13, currency: 'SGD', category: 'Professional Services', businessId: null, date: '2026-03-01', receiptUrl: null },
                      { id: 'exp-14', description: 'DigitalOcean - VPS Subscription', amount: 5.76, currency: 'SGD', category: 'Professional Services', businessId: null, date: '2026-03-01', receiptUrl: null },
                      { id: 'exp-15', description: 'OpenAI - Subscription', amount: 143.67, currency: 'SGD', category: 'Professional Services', businessId: null, date: '2026-03-02', receiptUrl: null },
                      { id: 'exp-16', description: 'OpenAI - Subscription', amount: 14.36, currency: 'SGD', category: 'Professional Services', businessId: null, date: '2026-03-02', receiptUrl: null },
                      { id: 'exp-17', description: 'Hostinger - VPS Subscription (2 years)', amount: 314.23, currency: 'SGD', category: 'Professional Services', businessId: null, date: '2026-03-05', receiptUrl: null },
                      { id: 'exp-18', description: 'Claude.AI - Subscription (1 year)', amount: 300.00, currency: 'SGD', category: 'Professional Services', businessId: null, date: '2026-03-10', receiptUrl: null },
                      { id: 'exp-19', description: 'Perplexity.AI - Credit Top Up for API Key for Research', amount: 13.27, currency: 'SGD', category: 'Professional Services', businessId: null, date: '2026-03-10', receiptUrl: null },
                      { id: 'exp-20', description: 'Claude.AI - CCY Conversion Fee', amount: 3.00, currency: 'SGD', category: 'Professional Services', businessId: null, date: '2026-03-11', receiptUrl: null },
                      { id: 'exp-21', description: 'Anthropic AI - Subscription', amount: 36.10, currency: 'SGD', category: 'Professional Services', businessId: null, date: '2026-03-14', receiptUrl: null },
                      { id: 'exp-22', description: 'Anthropic AI - Subscription', amount: 36.10, currency: 'SGD', category: 'Professional Services', businessId: null, date: '2026-03-14', receiptUrl: null },
                      { id: 'exp-23', description: 'Anthropic AI - Subscription', amount: 144.31, currency: 'SGD', category: 'Professional Services', businessId: null, date: '2026-03-18', receiptUrl: null },
                      { id: 'exp-24', description: 'Anthropic AI - Subscription', amount: 144.43, currency: 'SGD', category: 'Professional Services', businessId: null, date: '2026-03-21', receiptUrl: null },
                      { id: 'exp-25', description: 'Anthropic AI - Subscription', amount: 144.43, currency: 'SGD', category: 'Professional Services', businessId: null, date: '2026-03-22', receiptUrl: null },
                      { id: 'exp-26', description: 'Muay Thai', amount: 1500.00, currency: 'SGD', category: 'Other', businessId: kngId, date: '2026-03-23', receiptUrl: null },
                      { id: 'exp-27', description: 'Anthropic AI - Subscription', amount: 144.82, currency: 'SGD', category: 'Professional Services', businessId: null, date: '2026-03-23', receiptUrl: null },
                      { id: 'exp-28', description: 'Anthropic AI - Subscription', amount: 5.45, currency: 'SGD', category: 'Professional Services', businessId: null, date: '2026-03-24', receiptUrl: null },
                      { id: 'exp-29', description: 'Anthropic AI - Subscription', amount: 144.25, currency: 'SGD', category: 'Professional Services', businessId: null, date: '2026-03-25', receiptUrl: null },
                      { id: 'exp-30', description: 'Anthropic AI - Subscription', amount: 55.04, currency: 'SGD', category: 'Professional Services', businessId: null, date: '2026-03-28', receiptUrl: null },
                      { id: 'exp-31', description: 'Netlify - ReefsRivers website testing', amount: 11.97, currency: 'SGD', category: 'Professional Services', businessId: rrId, date: '2026-03-28', receiptUrl: null },
                      { id: 'exp-32', description: 'Anthropic AI - Subscription', amount: 11.00, currency: 'SGD', category: 'Professional Services', businessId: null, date: '2026-03-29', receiptUrl: null },
                      { id: 'exp-33', description: 'Netlify - Domain Hosting', amount: 11.60, currency: 'SGD', category: 'Professional Services', businessId: null, date: '2026-03-29', receiptUrl: null },
                      { id: 'exp-34', description: 'Phone Bill', amount: 84.58, currency: 'SGD', category: 'Utilities', businessId: null, date: '2026-03-29', receiptUrl: null },
                    ],
                  });
                }}
              >
                <Database size={16} /> Load Jan-Mar 2026 Claims Data
              </button>
            )}
          </div>
        )}

        {/* Top-ups history */}
        {pettyCash.topUps.length > 0 && (
          <div className="pc-topups-section">
            <h3 className="pc-section-title"><ArrowUpCircle size={16} /> Top-up History</h3>
            <div className="doc-items">
              {[...pettyCash.topUps].reverse().map((t) => (
                <div key={t.id} className="doc-item">
                  <div className="doc-item-left">
                    <ArrowUpCircle size={18} style={{ color: 'var(--sage)' }} />
                    <div>
                      <span className="doc-name">{sym}{fmtNum(t.amount)}</span>
                      <span className="doc-meta">{t.date} {t.note && `- ${t.note}`}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
